import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import ProfileDiscoveryDialog from '../connectors/ProfileDiscoveryDialog';
import ConnectorSuggestionsCard from './ConnectorSuggestionsCard';

export default function ConnectorsTab() {
  const { subsidiaries } = useSubsidiary();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [filterSubsidiary, setFilterSubsidiary] = useState('all');
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null); // Track which assignment is being edited
  const [editedSubsidiary, setEditedSubsidiary] = useState(null); // Track the selected subsidiary during edit

  useEffect(() => {
    loadConnectorAssignments();
  }, []);

  const loadConnectorAssignments = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/subsidiaries/connectors/all', {
        headers: { 'x-auth-token': token }
      });

      const data = await response.json();

      if (data.success) {
        setAssignments(data.assignments || []);
      } else {
        setError('Failed to load connector assignments');
      }
    } catch (err) {
      console.error('Error loading connector assignments:', err);
      setError('Failed to load connector assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    setFilterSubsidiary(event.target.value);
  };

  const handleConfigureProfiles = (connector) => {
    setSelectedConnector(connector);
    setProfileDialogOpen(true);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    setSelectedConnector(null);
  };

  const handleProfilesAssigned = () => {
    // Reload assignments after profiles are assigned
    loadConnectorAssignments();
  };

  const handleEditAssignment = (assignment) => {
    const assignmentKey = `${assignment.connector_id}-${assignment.profile_identifier || 'default'}`;
    setEditingAssignment(assignmentKey);
    setEditedSubsidiary(assignment.subsidiary_id);
  };

  const handleCancelEdit = () => {
    setEditingAssignment(null);
    setEditedSubsidiary(null);
  };

  const handleSaveEdit = async (assignment) => {
    if (!editedSubsidiary) {
      alert('Please select a subsidiary');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // First, remove the old assignment
      await fetch(`/api/subsidiaries/${assignment.subsidiary_id}/connectors/${assignment.connector_id}?profileIdentifier=${assignment.profile_identifier || ''}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });

      // Then create the new assignment
      const response = await fetch(`/api/subsidiaries/${editedSubsidiary}/connectors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          connectorId: assignment.connector_id,
          profileIdentifier: assignment.profile_identifier || '',
          profileName: assignment.profile_name || '',
          isPrimary: assignment.is_primary
        })
      });

      const data = await response.json();

      // POST returns 201 with the created row (no success field)
      if (response.ok) {
        setEditingAssignment(null);
        setEditedSubsidiary(null);
        loadConnectorAssignments(); // Reload to show updated assignment
      } else {
        alert('Failed to update assignment: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating assignment:', err);
      alert('Failed to update assignment. Please try again.');
    }
  };

  // Group assignments by connector for multi-profile display
  const groupedByConnector = assignments.reduce((acc, assignment) => {
    const key = assignment.connector_id;
    if (!acc[key]) {
      acc[key] = {
        connector_id: assignment.connector_id,
        connector_name: assignment.connector_name,
        platform_name: assignment.platform_name,
        platform_display_name: assignment.platform_display_name,
        supports_multi_profile: assignment.supports_multi_profile,
        platform_category: assignment.platform_category,
        brand_name: assignment.brand_name,
        profiles: []
      };
    }
    acc[key].profiles.push(assignment);
    return acc;
  }, {});

  const connectorsList = Object.values(groupedByConnector);

  // Identify multi-profile connectors
  const multiProfileConnectors = connectorsList.filter(c => c.supports_multi_profile);

  // Filter by subsidiary
  const filteredAssignments = filterSubsidiary === 'all'
    ? assignments
    : assignments.filter(a => a.subsidiary_id === parseInt(filterSubsidiary));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Auto-Suggested Assignments (QuickBooks-style) */}
      <ConnectorSuggestionsCard onRefresh={loadConnectorAssignments} />

      {/* Multi-Profile Connectors Section */}
      {multiProfileConnectors.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#f5f9ff', borderColor: 'primary.light', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  Multi-Profile Connectors ({multiProfileConnectors.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These connectors have multiple profiles that can be assigned to different subsidiaries
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Connector</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Profiles</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {multiProfileConnectors.map((connector) => {
                    const profilesWithData = connector.profiles.filter(p => p.profile_identifier && p.profile_identifier !== '');
                    const hasProfiles = profilesWithData.length > 0;

                    return (
                      <TableRow key={connector.connector_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {connector.connector_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={connector.platform_display_name} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{connector.brand_name || '-'}</TableCell>
                        <TableCell>
                          {hasProfiles ? (
                            <Typography variant="body2">
                              {profilesWithData.length} profile{profilesWithData.length !== 1 ? 's' : ''} configured
                            </Typography>
                          ) : (
                            <Chip
                              icon={<WarningIcon />}
                              label="Not configured"
                              color="warning"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {hasProfiles ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Ready"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Needs setup"
                              color="default"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => handleConfigureProfiles({
                              id: connector.connector_id,
                              name: connector.connector_name,
                              platform_id: connector.platform_id,
                              platform_name: connector.platform_name,
                              platform_display_name: connector.platform_display_name
                            })}
                          >
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* All Connector Assignments */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Connector Assignments ({filteredAssignments.length})
            </Typography>

            {/* Filter by subsidiary */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Filter by Subsidiary</InputLabel>
              <Select value={filterSubsidiary} onChange={handleFilterChange} label="Filter by Subsidiary">
                <MenuItem value="all">All Subsidiaries</MenuItem>
                {subsidiaries.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Connector</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subsidiary</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Profile</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Primary</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No connector assignments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map((assignment, index) => {
                    const assignmentKey = `${assignment.connector_id}-${assignment.profile_identifier || 'default'}`;
                    const isEditing = editingAssignment === assignmentKey;

                    return (
                      <TableRow key={`${assignmentKey}-${index}`}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {assignment.connector_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={assignment.platform_display_name} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{assignment.brand_name || '-'}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                              <Select
                                value={editedSubsidiary || ''}
                                onChange={(e) => setEditedSubsidiary(e.target.value)}
                              >
                                {subsidiaries.map((sub) => (
                                  <MenuItem key={sub.id} value={sub.id}>
                                    {sub.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {assignment.subsidiary_name}
                              {assignment.is_default_subsidiary && (
                                <Chip label="Default" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.profile_name || assignment.profile_identifier ? (
                            <Tooltip title={assignment.profile_identifier}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {assignment.profile_name || assignment.profile_identifier}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              <em>Default</em>
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.is_primary && <CheckCircleIcon color="success" fontSize="small" />}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSaveEdit(assignment)}
                                title="Save"
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={handleCancelEdit}
                                title="Cancel"
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleEditAssignment(assignment)}
                              title="Edit Subsidiary Assignment"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Profile Discovery Dialog */}
      {selectedConnector && (
        <ProfileDiscoveryDialog
          open={profileDialogOpen}
          onClose={handleProfileDialogClose}
          connector={selectedConnector}
          onProfilesAssigned={handleProfilesAssigned}
        />
      )}
    </Box>
  );
}
